import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import PermissionGuard from '../components/Auth/PermissionGuard';
import toast from 'react-hot-toast';

const AdminProducts = () => {
  const { user, getToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [expandedNames, setExpandedNames] = useState({});
  const [expandedDescs, setExpandedDescs] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const { formatPrice } = useCurrency();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    stock_quantity: '',
    image: '',
    image_url: '',
    video_url: '',
    barcode: '',
    stockcode: '',
    brand: '',
    department: '',
    vat_percentage: '0',
    discount: '0',
    original_price: '',
    promotion_id: '',
    promotion_type: '',
    promotion_start_date: '',
    promotion_end_date: '',
    express_delivery_days: '1',
    normal_delivery_days: '4'
  });
  const [formError, setFormError] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [imageLimits, setImageLimits] = useState({ min: 1, max: 10 });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [selectedAiImageStyle, setSelectedAiImageStyle] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (user) {
      fetchCategories();
      fetchImageLimits();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchProducts();
    }
  }, [user, debouncedSearchTerm]);

  const fetchImageLimits = async () => {
    try {
      const response = await fetch('/api/settings');
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to upload image');
      }
      const data = await response.json();
      if (data.success && data.data.settings) {
        const settings = data.data.settings;
        const min = settings.design_product_image_min?.value || settings.product_image_min?.value || 1;
        const max = settings.design_product_image_max?.value || settings.product_image_max?.value || 10;
        setImageLimits({ min: parseInt(min) || 1, max: parseInt(max) || 10 });
      }
    } catch (error) {
      console.error('Error fetching image limits:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const query = new URLSearchParams();
      if (debouncedSearchTerm) {
        query.set('search', debouncedSearchTerm);
      }
      const response = await fetch(`/api/products${query.toString() ? `?${query.toString()}` : ''}`);
      const data = await response.json();
      if (data.success) {
        // Enable detailed logging by uncommenting below
        // console.log('📥 Fetched products:', data.products.length);
        // if (data.products.length > 0) {
        //   const sample = data.products[0];
        //   const sampleFields = {
        //     id: sample.id,
        //     name: sample.name,
        //     barcode: sample.barcode,
        //     stockcode: sample.stockcode,
        //     brand: sample.brand,
        //     department: sample.department,
        //     vat_percentage: sample.vat_percentage,
        //     discount: sample.discount,
        //     original_price: sample.original_price,
        //     promotion_type: sample.promotion_type
        //   };
        //   console.log('📋 Sample product fields:', JSON.stringify(sampleFields, null, 2));
        //   console.log('📋 Full sample product object:', JSON.stringify(sample, null, 2));
        //   
        //   const latestProduct = data.products[data.products.length - 1];
        //   const latestFields = {
        //     id: latestProduct.id,
        //     name: latestProduct.name,
        //     barcode: latestProduct.barcode,
        //     stockcode: latestProduct.stockcode,
        //     brand: latestProduct.brand,
        //     department: latestProduct.department,
        //     vat_percentage: latestProduct.vat_percentage,
        //     discount: latestProduct.discount,
        //     original_price: latestProduct.original_price,
        //     promotion_type: latestProduct.promotion_type
        //   };
        //   console.log('📋 Latest product:', JSON.stringify(latestFields, null, 2));
        // }
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Get all subcategories flattened with parent category name
  const getAllSubcategories = (categories) => {
    let subcategories = [];
    categories.forEach(mainCat => {
      if (mainCat.subcategories && mainCat.subcategories.length > 0) {
        mainCat.subcategories.forEach(sub => {
          subcategories.push({
            ...sub,
            parentName: mainCat.name,
            displayName: `${mainCat.name} > ${sub.name}`
          });
          // Recursively get nested subcategories
          if (sub.subcategories && sub.subcategories.length > 0) {
            sub.subcategories.forEach(nestedSub => {
              subcategories.push({
                ...nestedSub,
                parentName: `${mainCat.name} > ${sub.name}`,
                displayName: `${mainCat.name} > ${sub.name} > ${nestedSub.name}`
              });
            });
          }
        });
      }
    });
    return subcategories;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setFormError(null);
    
    // Validate image requirements for new products
    if (!editingProduct) {
      const totalImages = productImages.length + (formData.image_url ? 1 : 0);
      if (totalImages < imageLimits.min) {
        setFormError(`Minimum ${imageLimits.min} image(s) required. Please upload at least ${imageLimits.min} image(s).`);
        return;
      }
    }
    
    try {
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct 
        ? `/api/products/${editingProduct.id}` 
        : '/api/products';

      // Enable detailed logging by uncommenting below
      // console.log('📤 ========== SENDING PRODUCT DATA ==========');
      // console.log('📤 Full formData:', JSON.stringify(formData, null, 2));
      // console.log('📤 Key fields check:');
      // console.log('  - barcode:', formData.barcode);
      // console.log('  - stockcode:', formData.stockcode);
      // console.log('  - brand:', formData.brand);
      // console.log('  - department:', formData.department);
      // console.log('  - vat_percentage:', formData.vat_percentage);
      // console.log('  - discount:', formData.discount);
      // console.log('  - original_price:', formData.original_price);
      // console.log('  - promotion_type:', formData.promotion_type);
      // console.log('==========================================');

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken(true)}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        fetchProducts(); // Refresh products list

        const createdProduct = data?.data?.product || data?.product || null;
        
        // If it's a new product, upload all selected images
        if (!editingProduct && createdProduct) {
          const newProduct = createdProduct;
          
          // Upload all images that were selected during creation
          if (productImages.length > 0 && newProduct.id) {
            try {
              // Save all image URLs to product_images table
              const imagePromises = productImages.map(async (img, index) => {
                if (img.image_url) {
                  const response = await fetch(`/api/upload/product/${newProduct.id}/image-url`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${getToken(true)}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ 
                      image_url: img.image_url, 
                      alt_text: img.original_name || `Product image ${index + 1}`,
                      sort_order: index + 1
                    })
                  });
                  return response.json();
                }
              });
              
              await Promise.all(imagePromises);
              await fetchProductImages(newProduct.id);
            } catch (error) {
              console.error('Error saving product images:', error);
              // Continue even if image save fails
            }
          }
          
          setEditingProduct(newProduct);
          toast.success(`Product created successfully! ${productImages.length > 0 ? `${productImages.length} image(s) uploaded.` : 'You can now upload images.'}`);
        } else {
          // For updates, refresh images
          if (editingProduct && editingProduct.id) {
            await fetchProductImages(editingProduct.id);
          }
          toast.success(editingProduct ? 'Product updated successfully!' : 'Product added successfully!');
        }
        
        // Don't reset form if we're keeping it open for image uploads
        if (editingProduct || !createdProduct) {
          resetForm();
        }
      } else {
        const detail = data.error || data.code || '';
        const errorMessage = data.message || 'An error occurred';
        setFormError(errorMessage + (detail ? ` (${detail})` : ''));
        // Show alert for duplicate barcode
        if (errorMessage.includes('Barcode') && errorMessage.includes('already exists')) {
          alert(`❌ ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setFormError('Network or server error while saving product');
    }
  };

  const fetchProductImages = async (productId) => {
    try {
      const response = await fetch(`/api/products/${productId}`);
      const data = await response.json();
      if (data.success && data.data.images) {
        setProductImages(data.data.images || []);
      } else {
        setProductImages([]);
      }
    } catch (error) {
      console.error('Error fetching product images:', error);
      setProductImages([]);
    }
  };

  const handleEdit = async (product) => {
    // Enable detailed logging by uncommenting below
    // console.log('📝 Editing product:', product);
    setEditingProduct(product);
    
    // Format datetime for datetime-local inputs (YYYY-MM-DDTHH:mm)
    const formatDateTimeLocal = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (e) {
        return '';
      }
    };
    
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      category_id: product.category_id || '',
      stock_quantity: product.stock_quantity || 0,
      image: product.image || '',
      image_url: product.image_url || product.image || '',
      video_url: product.video_url || '',
      barcode: product.barcode || '',
      stockcode: product.stockcode || '',
      brand: product.brand || '',
      department: product.department || '',
      vat_percentage: product.vat_percentage !== null && product.vat_percentage !== undefined ? String(product.vat_percentage) : '0',
      discount: product.discount !== null && product.discount !== undefined ? String(product.discount) : '0',
      original_price: product.original_price !== null && product.original_price !== undefined ? String(product.original_price) : '',
      promotion_id: product.promotion_id ? String(product.promotion_id) : '',
      promotion_type: product.promotion_type || '',
      promotion_start_date: formatDateTimeLocal(product.promotion_start_date),
      promotion_end_date: formatDateTimeLocal(product.promotion_end_date),
      express_delivery_days: product.express_delivery_days ? String(product.express_delivery_days) : '1',
      normal_delivery_days: product.normal_delivery_days ? String(product.normal_delivery_days) : '4'
    });
    // Fetch product images
    if (product.id) {
      await fetchProductImages(product.id);
    } else {
      setProductImages([]);
    }
    
    // Enable detailed logging by uncommenting below
    // console.log('📋 Form data set:', {
    //   barcode: product.barcode,
    //   stockcode: product.stockcode,
    //   brand: product.brand,
    //   department: product.department,
    //   vat_percentage: product.vat_percentage,
    //   discount: product.discount,
    //   original_price: product.original_price,
    //   promotion_type: product.promotion_type
    // });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken(true)}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        fetchProducts(); // Refresh products list
        alert('Product deleted successfully!');
      } else {
        alert('Error deleting product: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      stock_quantity: '',
      image: '',
      image_url: '',
      video_url: '',
      barcode: '',
      stockcode: '',
      brand: '',
      department: '',
      vat_percentage: '0',
      discount: '0',
      original_price: '',
      promotion_id: '',
      promotion_type: '',
      promotion_start_date: '',
      promotion_end_date: '',
      express_delivery_days: '1',
      normal_delivery_days: '4'
    });
    setProductImages([]);
    setEditingProduct(null);
    setShowAddForm(false);
    setAiSuggestions(null);
    setSelectedAiImageStyle(null);
    setAiLoading(false);
  };

  const handleMultipleImageUploadForNew = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    console.log(`📤 [Frontend] Selected ${files.length} file(s) for new product`);

    // Check limits
    const totalAfterUpload = productImages.length + files.length;
    if (totalAfterUpload > imageLimits.max) {
      alert(`Maximum ${imageLimits.max} images allowed. Current: ${productImages.length}, Trying to add: ${files.length}`);
      return;
    }

    setUploadingImages(true);
    try {
      // Upload all images and get URLs
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/upload/image', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken(true)}`
          },
          body: formData
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to upload image');
        }
        const data = await response.json();
        if (data.success) {
          return {
            image_url: data.data.image_url,
            preview: data.data.image_url,
            filename: data.data.filename,
            original_name: data.data.original_name
          };
        } else {
          throw new Error(data.message || 'Failed to upload image');
        }
      });

      const uploadedImages = await Promise.all(uploadPromises);
      const newImages = [...productImages, ...uploadedImages];
      
      setProductImages(newImages);
      
      // Set first image as primary
      if (newImages.length > 0 && !formData.image_url) {
        setFormData(prev => ({
          ...prev,
          image_url: newImages[0].image_url,
          image: newImages[0].image_url
        }));
      }

      toast.success(`Successfully uploaded ${uploadedImages.length} image(s)`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images: ' + error.message);
    } finally {
      setUploadingImages(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      const uploadData = new FormData();
      uploadData.append('video', file);

      const response = await fetch('/api/upload/video', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken(true)}`
        },
        body: uploadData
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to upload video');
      }

      setFormData((prev) => ({
        ...prev,
        video_url: data.data.video_url
      }));
      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(`Failed to upload video: ${error.message}`);
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const handleMultipleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    console.log(`📤 [Frontend] Selected ${files.length} file(s) for upload`);

    if (!editingProduct || !editingProduct.id) {
      alert('Please save the product first before uploading multiple images');
      return;
    }

    // Check limits
    const totalAfterUpload = productImages.length + files.length;
    if (totalAfterUpload > imageLimits.max) {
      alert(`Maximum ${imageLimits.max} images allowed. Current: ${productImages.length}, Trying to add: ${files.length}`);
      return;
    }

    setUploadingImages(true);
    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        console.log(`📤 [Frontend] Appending file ${index + 1}/${files.length}: ${file.name} (${file.size} bytes)`);
        formData.append('images', file);
      });

      console.log(`📤 [Frontend] Sending ${files.length} file(s) to /api/upload/product/${editingProduct.id}/images`);

      const response = await fetch(`/api/upload/product/${editingProduct.id}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken(true)}`
        },
        body: formData
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to upload images');
      }
      const data = await response.json();
      console.log('📥 [Frontend] Upload response:', data);

      if (data.success) {
        const uploadedCount = data.data?.images?.length || files.length;
        await fetchProductImages(editingProduct.id);
        toast.success(`Successfully uploaded ${uploadedCount} image(s)`);
      } else {
        console.error('❌ [Frontend] Upload failed:', data);
        toast.error(data.message || 'Failed to upload images');
        if (data.errors && data.errors.length > 0) {
          console.error('❌ [Frontend] Upload errors:', data.errors);
        }
      }
    } catch (error) {
      console.error('❌ [Frontend] Error uploading images:', error);
      toast.error('Failed to upload images: ' + error.message);
    } finally {
      setUploadingImages(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!editingProduct || !editingProduct.id) return;
    
    if (productImages.length <= imageLimits.min) {
      toast.error(`Minimum ${imageLimits.min} image(s) required. Cannot delete.`);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeletingImageId(imageId);
    try {
      const response = await fetch(`/api/upload/product/${editingProduct.id}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken(true)}`
        }
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to delete image');
      }
      const data = await response.json();

      if (data.success) {
        // Remove from local state immediately for better UX
        setProductImages(prev => prev.filter(img => img.id !== imageId));
        toast.success('Image deleted successfully');
        // Refresh from server to ensure consistency
        await fetchProductImages(editingProduct.id);
      } else {
        toast.error(data.message || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image. Please try again.');
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleGenerateAI = async () => {
    if (productImages.length === 0) {
      toast.error('Upload at least one photo first');
      return;
    }

    setAiLoading(true);
    setAiSuggestions(null);
    try {
      const filenames = productImages.map((img) => img.filename).filter(Boolean);
      if (filenames.length === 0) {
        throw new Error('Selected images are missing filenames - try re-uploading');
      }

      const selectedCategory = getAllSubcategories(categories).find(
        (c) => String(c.id) === String(formData.category_id)
      );

      const response = await fetch('/api/ai/product-assist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken(true)}`
        },
        body: JSON.stringify({
          filenames,
          category_name: selectedCategory?.displayName || selectedCategory?.name || ''
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'AI generation failed');
      }

      setAiSuggestions(data.data);

      if (data.data.name && !formData.name) {
        setFormData((prev) => ({ ...prev, name: data.data.name }));
      }
      if (data.data.description && !formData.description) {
        setFormData((prev) => ({ ...prev, description: data.data.description }));
      }

      if (data.data.errors && data.data.errors.length > 0) {
        toast.error(`AI generated partial results (${data.data.errors.length} step(s) failed) - review below`);
      } else {
        toast.success('AI suggestions ready - review and select an image below');
      }
    } catch (error) {
      console.error('AI generate error:', error);
      toast.error('Failed to generate AI suggestions: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSelectAiImage = (variant) => {
    setSelectedAiImageStyle(variant.style);
    const newImage = {
      image_url: variant.image_url,
      preview: variant.image_url,
      filename: variant.image_url.split('/').pop(),
      original_name: `${variant.label}.png`
    };
    setProductImages((prev) => [newImage, ...prev]);
    setFormData((prev) => ({ ...prev, image_url: variant.image_url, image: variant.image_url }));
    toast.success(`"${variant.label}" set as the product image`);
  };

  return (
    <PermissionGuard module="products">
      <div className="min-h-screen bg-gray-100 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Product Management</h1>
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full rounded-lg bg-blue-600 px-6 py-2 text-white transition duration-300 hover:bg-blue-700 sm:w-auto"
          >
            Add New Product
          </button>
        </div>

        <div className="mb-6">
          <div className="relative max-w-xl">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products by name or description..."
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 pr-20 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {debouncedSearchTerm ? `Showing results for \"${debouncedSearchTerm}\"` : 'Showing all products'}
          </p>
        </div>

        {/* Add/Edit Product Form */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl max-h-screen overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <div className="p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
                    {formError}
                  </div>
                )}

                {/* Image Upload - moved first so AI generation can use the photo right away */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Images
                    <span className="text-xs text-gray-500 ml-2">
                      (Min: {imageLimits.min}, Max: {imageLimits.max})
                    </span>
                  </label>

                  {/* Unified Image Upload Section */}
                  {editingProduct && editingProduct.id ? (
                    // For existing products: Show multiple image upload
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Images
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleMultipleImageUpload}
                          disabled={uploadingImages || (productImages.length >= imageLimits.max)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                        {uploadingImages && (
                          <p className="text-sm text-blue-600 mt-2">Uploading images...</p>
                        )}
                        {productImages.length >= imageLimits.max && (
                          <p className="text-sm text-red-600 mt-2">
                            Maximum {imageLimits.max} images reached
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Current: {productImages.length} / {imageLimits.max} images
                        </p>
                      </div>

                      {/* Product Images Gallery */}
                      {productImages.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-semibold text-gray-700">Product Images ({productImages.length})</h4>
                          <div className="grid grid-cols-4 gap-4">
                            {productImages.map((img, index) => (
                              <div key={img.id} className="relative group border-2 border-gray-200 rounded-lg overflow-hidden hover:border-indigo-400 transition-all">
                                <img
                                  src={img.image_url}
                                  alt={img.alt_text || `Product image ${index + 1}`}
                                  className="w-full h-32 object-cover"
                                  onError={(e) => {
                                    e.target.src = '/api/placeholder/150/150';
                                  }}
                                />
                                {/* Delete Button - Always visible */}
                                <div className="absolute top-2 right-2">
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteImage(img.id)}
                                    disabled={productImages.length <= imageLimits.min || deletingImageId === img.id}
                                    className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all transform hover:scale-110 active:scale-95"
                                    title={productImages.length <= imageLimits.min ? `Minimum ${imageLimits.min} image(s) required` : "Delete image"}
                                  >
                                    {deletingImageId === img.id ? (
                                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                                {/* Image Number Badge */}
                                <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded font-semibold">
                                  {index + 1}
                                </div>
                                {/* Primary Image Badge */}
                                {index === 0 && (
                                  <div className="absolute top-2 left-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded font-semibold">
                                    Primary
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          {productImages.length <= imageLimits.min && (
                            <p className="text-xs text-amber-600 mt-2">
                              ⚠️ Minimum {imageLimits.min} image(s) required. Cannot delete more images.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    // For new products: Show multiple image upload
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Images ({imageLimits.min} - {imageLimits.max} images required)
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleMultipleImageUploadForNew}
                          disabled={uploadingImages}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                        {uploadingImages && (
                          <p className="text-sm text-blue-600 mt-2">Uploading images...</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          You can select multiple images at once. Minimum {imageLimits.min} image(s) required, maximum {imageLimits.max} allowed.
                        </p>
                      </div>

                      {/* Create AI Images button */}
                      {productImages.length > 0 && (
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                          <button
                            type="button"
                            onClick={handleGenerateAI}
                            disabled={aiLoading}
                            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {aiLoading ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating...
                              </>
                            ) : (
                              'Create AI Images'
                            )}
                          </button>
                          <p className="text-xs text-indigo-700 mt-2">
                            Uses your first uploaded photo to draft a name, description, and a few styled image options. Everything stays editable before you save.
                          </p>

                          {aiSuggestions && aiSuggestions.images && aiSuggestions.images.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <h4 className="text-sm font-semibold text-gray-700">AI image options - click one to use it</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {aiSuggestions.images.map((variant) => (
                                  <button
                                    type="button"
                                    key={variant.style}
                                    onClick={() => handleSelectAiImage(variant)}
                                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                                      selectedAiImageStyle === variant.style ? 'border-indigo-600 ring-2 ring-indigo-300' : 'border-gray-200 hover:border-indigo-400'
                                    }`}
                                  >
                                    <img
                                      src={variant.image_url}
                                      alt={variant.label}
                                      className="w-full h-28 object-cover"
                                    />
                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-[11px] px-1.5 py-1 text-left">
                                      {variant.label}
                                      {variant.needsReview && (
                                        <span className="block text-amber-300">Review closely - AI-generated model shot</span>
                                      )}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {aiSuggestions && aiSuggestions.errors && aiSuggestions.errors.length > 0 && (
                            <p className="text-xs text-red-600 mt-2">
                              {aiSuggestions.errors.length} generation step(s) failed - you can still upload photos manually.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Preview uploaded images for new product */}
                      {productImages.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-semibold text-gray-700">Selected Images ({productImages.length})</h4>
                          <div className="grid grid-cols-4 gap-4">
                            {productImages.map((img, index) => (
                              <div key={index} className="relative group border-2 border-gray-200 rounded-lg overflow-hidden">
                                <img
                                  src={img.preview || img.image_url}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-32 object-cover"
                                  onError={(e) => {
                                    e.target.src = '/api/placeholder/150/150';
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newImages = productImages.filter((_, i) => i !== index);
                                    setProductImages(newImages);
                                    // Update formData image_url to first remaining image
                                    if (newImages.length > 0) {
                                      setFormData(prev => ({
                                        ...prev,
                                        image_url: newImages[0].image_url || newImages[0].preview || ''
                                      }));
                                    } else {
                                      setFormData(prev => ({ ...prev, image_url: '' }));
                                    }
                                  }}
                                  disabled={productImages.length <= imageLimits.min}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                  title={productImages.length <= imageLimits.min ? `Minimum ${imageLimits.min} image(s) required` : "Remove image"}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                                <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded font-semibold">
                                  {index + 1}
                                </div>
                                {index === 0 && (
                                  <div className="absolute top-2 left-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded font-semibold">
                                    Primary
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          {productImages.length < imageLimits.min && (
                            <p className="text-xs text-amber-600 mt-2">
                              ⚠️ Minimum {imageLimits.min} image(s) required. Currently: {productImages.length}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Alternative: Enter Image URL */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Or Enter Image URL (Optional - will be added as first image)
                        </label>
                        <input
                          type="text"
                          name="image_url"
                          value={formData.image_url}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com/image.jpg"
                        />
                        {formData.image_url && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-600 mb-1">URL Preview:</p>
                            <img
                              src={formData.image_url}
                              alt="URL Preview"
                              className="h-32 w-32 object-cover rounded border border-gray-300"
                              onError={(e) => {
                                e.target.src = '/api/placeholder/150/150';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter product name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter product description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price ({formatPrice(0).replace(/\d+/g, '')})
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a subcategory (required)</option>
                      {getAllSubcategories(categories).map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.displayName}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Products must be assigned to a subcategory, not a main category
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stock Quantity
                    </label>
                    <input
                      type="number"
                      name="stock_quantity"
                      value={formData.stock_quantity}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Barcode
                    </label>
                    <input
                      type="text"
                      name="barcode"
                      value={formData.barcode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter barcode"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stock Code
                    </label>
                    <input
                      type="text"
                      name="stockcode"
                      value={formData.stockcode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter stock code"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brand
                    </label>
                    <input
                      type="text"
                      name="brand"
                      value={formData.brand}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter brand name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter department"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      VAT/GST Percentage (India)
                    </label>
                    <input
                      type="number"
                      name="vat_percentage"
                      value={formData.vat_percentage}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter GST percentage (e.g., 5, 12, 18, 28)</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Original Price (Before Discount)
                    </label>
                    <input
                      type="number"
                      name="original_price"
                      value={formData.original_price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Amount
                    </label>
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Promotion Engine Fields */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Promotion Engine</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Promotion ID
                      </label>
                      <input
                        type="number"
                        name="promotion_id"
                        value={formData.promotion_id}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter promotion ID"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Promotion Type
                      </label>
                      <select
                        name="promotion_type"
                        value={formData.promotion_type}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select promotion type</option>
                        <option value="percentage">Percentage Discount</option>
                        <option value="fixed">Fixed Amount Discount</option>
                        <option value="buy_one_get_one">Buy One Get One</option>
                        <option value="free_shipping">Free Shipping</option>
                        <option value="seasonal">Seasonal Sale</option>
                        <option value="flash_sale">Flash Sale</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Promotion Start Date
                      </label>
                      <input
                        type="datetime-local"
                        name="promotion_start_date"
                        value={formData.promotion_start_date}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Promotion End Date
                      </label>
                      <input
                        type="datetime-local"
                        name="promotion_end_date"
                        value={formData.promotion_end_date}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Settings */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Express Delivery Days
                      </label>
                      <input
                        type="number"
                        name="express_delivery_days"
                        value={formData.express_delivery_days}
                        onChange={handleInputChange}
                        min="1"
                        max="7"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Days from today for express delivery (default: 1 = Tomorrow)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Normal Delivery Days
                      </label>
                      <input
                        type="number"
                        name="normal_delivery_days"
                        value={formData.normal_delivery_days}
                        onChange={handleInputChange}
                        min="1"
                        max="30"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="4"
                      />
                      <p className="text-xs text-gray-500 mt-1">Days from today for normal delivery (default: 4 days)</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Video
                    <span className="text-xs text-gray-500 ml-2">
                      (Optional, short MP4/WebM/MOV)
                    </span>
                  </label>

                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Short Video
                      </label>
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        onChange={handleVideoUpload}
                        disabled={uploadingVideo}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      />
                      {uploadingVideo && (
                        <p className="text-sm text-blue-600 mt-2">Uploading video...</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Use a short product clip. Recommended MP4/WebM under 20MB.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Or Enter Video URL
                      </label>
                      <input
                        type="text"
                        name="video_url"
                        value={formData.video_url}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/product-video.mp4"
                      />
                    </div>

                    {formData.video_url && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Video Preview:</p>
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, video_url: '' }))}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Remove video
                          </button>
                        </div>
                        <video
                          src={formData.video_url}
                          controls
                          className="w-full max-h-64 rounded border border-gray-300 bg-black"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300"
                  >
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Products Table */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="xl:hidden divide-y divide-gray-200">
            {loading ? (
              <div className="px-4 py-10 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No products found
              </div>
            ) : (
              products.map((product) => (
                <div key={product.id} className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <img
                      className="h-14 w-14 rounded-lg object-cover"
                      src={product.image || '/api/placeholder/56/56'}
                      alt={product.name}
                    />
                    <div className="min-w-0 flex-1">
                      {(() => {
                        const nameLimit = 90;
                        const descLimit = 140;
                        const isLongName = (product.name || '').length > nameLimit;
                        const isLongDesc = (product.description || '').length > descLimit;
                        const nameExpanded = !!expandedNames[product.id];
                        const descExpanded = !!expandedDescs[product.id];

                        return (
                          <>
                            <div className="text-base font-semibold text-gray-900 break-words whitespace-normal">
                              {isLongName && !nameExpanded
                                ? `${product.name.slice(0, nameLimit)}...`
                                : product.name}
                              {isLongName && (
                                <button
                                  id={`mobile-name-toggle-${product.id}`}
                                  onClick={() => setExpandedNames((prev) => ({ ...prev, [product.id]: !nameExpanded }))}
                                  className="ml-1 text-blue-600 hover:underline text-xs font-normal"
                                >
                                  {nameExpanded ? 'Read less' : 'Read more'}
                                </button>
                              )}
                            </div>

                            <div className="mt-1 text-sm text-gray-500 break-words whitespace-normal">
                              {isLongDesc && !descExpanded
                                ? `${product.description.slice(0, descLimit)}...`
                                : (product.description || 'No description available')}
                              {isLongDesc && (
                                <button
                                  id={`mobile-desc-toggle-${product.id}`}
                                  onClick={() => setExpandedDescs((prev) => ({ ...prev, [product.id]: !descExpanded }))}
                                  className="ml-1 text-blue-500 hover:underline text-xs"
                                >
                                  {descExpanded ? 'Read less' : 'Read more'}
                                </button>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Category</p>
                      <p className="mt-1 font-medium text-gray-900">
                        {product.parent_category_name && product.category_name
                          ? `${product.parent_category_name} > ${product.category_name}`
                          : product.category_name || 'No Category'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Price</p>
                      <p className="mt-1 font-semibold text-gray-900">{formatPrice(product.price)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Stock</p>
                      <p className="mt-1 font-medium text-gray-900">{product.stock_quantity || 0}</p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={() => handleEdit(product)}
                      className="w-full rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 sm:w-auto"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="w-full rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 sm:w-auto"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden xl:block">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[46%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="w-[18%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="w-[10%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="w-[8%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="w-[18%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No products found
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={product.image || '/api/placeholder/40/40'}
                            alt={product.name}
                          />
                        </div>
                        <div className="ml-4 min-w-0 max-w-full">
                          {(() => {
                            const nameLimit = 90;
                            const descLimit = 140;
                            const isLongName = (product.name || '').length > nameLimit;
                            const isLongDesc = (product.description || '').length > descLimit;
                            const nameExpanded = !!expandedNames[product.id];
                            const descExpanded = !!expandedDescs[product.id];

                            return (
                              <>
                                <div className="text-sm font-medium leading-5 text-gray-900 break-words whitespace-normal">
                                  {isLongName && !nameExpanded
                                    ? `${product.name.slice(0, nameLimit)}...`
                                    : product.name}
                                  {isLongName && (
                                    <button
                                      id={`desktop-name-toggle-${product.id}`}
                                      onClick={() => setExpandedNames((prev) => ({ ...prev, [product.id]: !nameExpanded }))}
                                      className="ml-1 text-blue-600 hover:underline text-xs font-normal"
                                    >
                                      {nameExpanded ? 'Read less' : 'Read more'}
                                    </button>
                                  )}
                                </div>

                                <div className="mt-1 max-w-md text-sm text-gray-500 break-words whitespace-normal">
                                  {isLongDesc && !descExpanded
                                    ? `${product.description.slice(0, descLimit)}...`
                                    : (product.description || 'No description available')}
                                  {isLongDesc && (
                                    <button
                                      id={`desktop-desc-toggle-${product.id}`}
                                      onClick={() => setExpandedDescs((prev) => ({ ...prev, [product.id]: !descExpanded }))}
                                      className="ml-1 text-blue-500 hover:underline text-xs"
                                    >
                                      {descExpanded ? 'Read less' : 'Read more'}
                                    </button>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 break-words whitespace-normal align-top">
                      {product.parent_category_name && product.category_name 
                        ? `${product.parent_category_name} > ${product.category_name}`
                        : product.category_name || 'No Category'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                      {formatPrice(product.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                      {product.stock_quantity || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium align-top">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      </div>
    </PermissionGuard>
  );
};

export default AdminProducts;
